/**
 * Candidate Dashboard
 * Dashboard for candidates to track their application
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import interviewService from '../services/interviewService';

const CandidateDashboard = () => {
  // Map backend stage to display name
  const getStageDisplayName = (stage) => {
    if (!stage) return '';
    const map = {
      REGISTRATION: 'APPLIED',
      HR_SHORTLISTING: 'SHORTLISTED',
      AGREEMENT: 'AGREEMENT',
      APPLICATION: 'APPLICATION',
      INTERVIEW: 'INTERVIEW',
      OFFER_LETTER: 'OFFER LETTER',
      PAYMENT: 'PAYMENT',
      DOCUMENT_VERIFICATION: 'DOCUMENT VERIFICATION',
      EXAM: 'EXAM',
      MEDICAL: 'MEDICAL',
      TPC_PRACTISING: 'TPC PRACTISING',
      ESD_APPROVAL: 'ESD APPROVAL',
      VISA_PROCESS: 'VISA PROCESS',
      TICKET: 'TICKET',
      DEPARTURE: 'DEPARTURE',
      COMPLETED: 'COMPLETED',
      REJECTED: 'REJECTED',
    };
    return map[stage] || stage.replace(/_/g, ' ');
  };
  // Consistent display name logic as header
  const getDisplayName = () => {
    const joinedName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return (
      user?.name ||
      user?.fullName ||
      (joinedName || null) ||
      (user?.email ? user.email.split('@')[0] : null) ||
      user?.employeeId ||
      user?.phoneNumber ||
      'User'
    );
  };
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeApplication, setActiveApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentCount, setDocumentCount] = useState(0);
  const [interviewCount, setInterviewCount] = useState(0);
  const [totalApplications, setTotalApplications] = useState(0);

  useEffect(() => {
    fetchActiveApplication();
  }, []);

  const fetchActiveApplication = async () => {
    try {
      // Fetch all applications to get total count and find active one
      const response = await applicationService.getApplications({ limit: 50 }); // Get more to ensure we have all
      if (response.success) {
        // Set total applications count
        setTotalApplications(response.total);

        if (response.applications.length > 0) {
          // Get the most recent non-completed/rejected application
          const active = response.applications.find(
            app => app.currentStage !== 'COMPLETED' && app.currentStage !== 'REJECTED'
          );
          const selectedApp = active || response.applications[0];
          setActiveApplication(selectedApp);

          // Fetch document count
          if (selectedApp) {
            fetchDocumentCount(selectedApp._id);
            fetchInterviewCount(selectedApp._id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load application:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentCount = async (applicationId) => {
    try {
      const response = await applicationService.getApplicationDocuments(applicationId);
      if (response && Array.isArray(response)) {
        setDocumentCount(response.length);
      } else if (response?.data && Array.isArray(response.data)) {
        setDocumentCount(response.data.length);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  const fetchInterviewCount = async (applicationId) => {
    try {
      const interview = await interviewService.getApplicationInterview(applicationId);
      // getApplicationInterview returns a single interview or null
      setInterviewCount(interview ? 1 : 0);
    } catch (err) {
      console.error('Failed to load interviews:', err);
      setInterviewCount(0);
    }
  };

  return (
    <MainLayout>
      <div className="flex justify-between items-center mb-xl p-lg px-xl bg-surface rounded-lg shadow-sm border border-border-light">
        <div>
          <h1 className="text-[28px] text-primary m-0 font-semibold tracking-[-0.5px]">Candidate Dashboard</h1>
          <p className="text-sm text-text-secondary mt-1 font-normal">Track your overseas recruitment journey</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-lg max-w-full">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-lg col-span-full">
          <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-green">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Total Applications</h3>
              <p className="text-2xl text-primary m-0 font-bold leading-none">
                {loading ? 'Loading...' : totalApplications}
              </p>
            </div>
          </div>

          <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-accent-green">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Application Status</h3>
              <p className="text-2xl text-primary m-0 font-bold leading-none">
                {loading
                  ? 'Loading...'
                  : activeApplication
                    ? getStageDisplayName(activeApplication.currentStage)
                    : 'Not Started'}
              </p>
            </div>
          </div>

          <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-green">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Documents</h3>
              <p className="text-2xl text-primary m-0 font-bold leading-none">
                {loading ? 'Loading...' : `${documentCount} Uploaded`}
              </p>
            </div>
          </div>

          <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent-green">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Interviews</h3>
              <p className="text-2xl text-primary m-0 font-bold leading-none">
                {loading ? 'Loading...' : interviewCount > 0 ? `${interviewCount} Scheduled` : 'No Scheduled'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
          <h3 className="text-lg text-primary mb-lg pb-md border-b-2 border-border-light font-semibold">Your Profile</h3>
          <div className="flex justify-between items-center py-md border-b border-border-light">
            <span className="text-text-secondary font-medium text-sm">Name:</span>
            <span className="text-text-primary font-semibold text-sm">{getDisplayName()}</span>
          </div>
          {user?.email && (
            <div className="flex justify-between items-center py-md border-b border-border-light">
              <span className="text-text-secondary font-medium text-sm">Email:</span>
              <span className="text-text-primary font-semibold text-sm">{user.email}</span>
            </div>
          )}
          <div className="flex justify-between items-center py-md border-b border-border-light">
            <span className="text-text-secondary font-medium text-sm">Role:</span>
            <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-status-processing-bg text-status-processing">CANDIDATE</span>
          </div>
          <button
            className="mt-4 px-5 py-2.5 bg-accent-green text-white border-none rounded-md text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-accent-green-dark hover:-translate-y-px"
            onClick={() => navigate('/candidate/profile')}
          >
            View Full Profile
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default CandidateDashboard;
